import styles from "./MaritimeBroadcastRenderer.module.css";

export type WarningGroup = {
  areaNames: string[];
  description: string;
};

export type AreaLine = {
  /** One name when unique forecast, multiple when grouped (identical forecasts). */
  names: string[];
  text: string;
  isLittleChange?: boolean;
};

export type BroadcastPeriod = {
  header: string;
  areas: AreaLine[];
};

export type MaritimeBroadcastRendererProps = {
  headerSubtitle?: string;
  issuedLine: string;
  warningHeader: string;
  /** Populated when at least one area has a warning. */
  warningGroups: WarningGroup[];
  /** Sub-header for areas with no warning, e.g. "Overige districten:" — present when some areas have warnings. */
  otherAreasHeader?: string;
  /** Text under otherAreasHeader, e.g. "geen waarschuwing". */
  otherAreasText?: string;
  /** Shown when no area has an active warning. */
  noWarningsText: string;
  hasWarnings: boolean;
  synopsisHeader: string;
  synopsis?: string;
  periods: BroadcastPeriod[];
  footerLines: string[];
};

const MaritimeBroadcastRenderer = ({
  headerSubtitle,
  issuedLine,
  warningHeader,
  warningGroups,
  otherAreasHeader,
  otherAreasText,
  noWarningsText,
  hasWarnings,
  synopsisHeader,
  synopsis,
  periods,
  footerLines,
}: MaritimeBroadcastRendererProps) => (
  <article className={styles.root}>
    <header className={styles.header}>
      {headerSubtitle && <p className={styles.subtitle}>{headerSubtitle}</p>}
      <p className={styles.issuedLine}>{issuedLine}</p>
    </header>

    <section className={styles.section}>
      <p className={styles.sectionHeader}>{warningHeader}</p>
      {hasWarnings ? (
        <>
          {warningGroups.map((group, i) => (
            <div key={i} className={styles.warningGroup}>
              <p className={styles.warningAreaNames}>{group.areaNames.join(" ")}</p>
              <p className={styles.warningDescription}>{group.description}</p>
            </div>
          ))}
          {otherAreasHeader && (
            <div className={styles.otherAreas}>
              <p className={styles.otherAreasHeader}>{otherAreasHeader}</p>
              <p className={styles.otherAreasText}>{otherAreasText}</p>
            </div>
          )}
        </>
      ) : (
        <p className={styles.noWarningsText}>{noWarningsText}</p>
      )}
    </section>

    <section className={styles.section}>
      <p className={styles.sectionHeader}>{synopsisHeader}</p>
      {synopsis && <p className={styles.synopsis}>{synopsis}</p>}
    </section>

    {periods.map((period, pi) => (
      <section key={pi} className={styles.section}>
        <p className={styles.periodHeader}>{period.header}</p>
        {period.areas.map((area, ai) => (
          <div key={ai} className={styles.areaBlock}>
            <p className={styles.areaNames}>{area.names.join(" ")}</p>
            <p className={styles.areaText}>
              {area.isLittleChange ? <em>{area.text}</em> : area.text}
            </p>
          </div>
        ))}
      </section>
    ))}

    <footer className={styles.footer}>
      {footerLines.map((line, i) => (
        <p key={i} className={styles.footerLine}>
          {line}
        </p>
      ))}
    </footer>
  </article>
);

export default MaritimeBroadcastRenderer;
